import { motion } from "framer-motion";
import { Bot, CheckCircle2, ShieldCheck, ArrowUpRight, Timer, Smile, Clock, ArrowUp, ArrowDown } from "lucide-react";
import { InfoTip } from "./InfoTip";
import { DEFINITIONS, fmtHours, fmtNum, fmtPct, fmtScore } from "./definitions";

const GROUP_DEFS = [DEFINITIONS.ai_resolved, DEFINITIONS.ai_assisted, DEFINITIONS.human_only, DEFINITIONS.sender];

const CARDS = [
  { key: "ai_handled", label: "Tickets handled by AI", icon: Bot, kind: "count", better: "up", def: DEFINITIONS.total,
    hint: (k) => `${fmtPct(k.ai_coverage)} of ${fmtNum(k.total_tickets)} conversations` },
  { key: "closed_via_ai", label: "Closed via AI", icon: CheckCircle2, kind: "count", better: "up", def: DEFINITIONS.closed_via_ai,
    hint: (k) => `${fmtNum(k.ai_resolved)} resolved + ${fmtNum(k.closed_via_ai - k.ai_resolved)} assisted` },
  { key: "ai_resolved", label: "No human interaction", icon: ShieldCheck, kind: "count", better: "up", def: DEFINITIONS.containment,
    hint: (k) => `${fmtPct(k.containment_rate)} containment rate` },
  { key: "escalation_rate", label: "Escalation rate", icon: ArrowUpRight, kind: "rate", better: "down", def: DEFINITIONS.escalation,
    hint: () => "of AI-handled handed to a human" },
  { key: "ai_ttc_avg_h", label: "Avg time to close (AI)", icon: Timer, kind: "time", better: "down", def: DEFINITIONS.ttc,
    hint: (k) => `Median ${fmtHours(k.ai_ttc_median_h)}` },
  { key: "ai_csat_avg", label: "CSAT (AI-resolved)", icon: Smile, kind: "score", better: "up", def: DEFINITIONS.csat,
    hint: (k) => `${fmtPct(k.ai_csat_response_rate)} response rate · ${fmtNum(k.ai_csat_responses)} rated` },
  { key: "hours_saved", label: "Agent hours saved", icon: Clock, kind: "count", better: "up", def: DEFINITIONS.hours_saved,
    hint: (_, s) => `@ ${s.handle_time_minutes} min per ticket` },
];

const display = (card, v) => {
  if (card.kind === "rate") return fmtPct(v);
  if (card.kind === "time") return fmtHours(v);
  if (card.kind === "score") return fmtScore(v);
  return fmtNum(v);
};

const Delta = ({ card, cur, prev, testId }) => {
  if (prev === null || prev === undefined || cur === null || cur === undefined) {
    return <span className="text-xs text-slate-400" data-testid={testId}>no prior data</span>;
  }
  const diff = card.kind === "rate" || card.kind === "score" ? cur - prev : prev === 0 ? null : ((cur - prev) / prev) * 100;
  if (diff === null) return <span className="text-xs text-slate-400" data-testid={testId}>new vs 0</span>;
  const good = card.better === "up" ? diff >= 0 : diff <= 0;
  const Icon = diff >= 0 ? ArrowUp : ArrowDown;
  const unit = card.kind === "rate" ? " pts" : card.kind === "score" ? "" : "%";
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${good ? "text-emerald-600" : "text-rose-600"}`} data-testid={testId}>
      <Icon className="w-3 h-3" />
      {Math.abs(diff).toFixed(1)}{unit}
    </span>
  );
};

export const KpiCards = ({ kpis, previous, settings }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 2xl:grid-cols-7 gap-3" data-testid="kpi-cards">
    {CARDS.map((card, i) => {
      const Icon = card.icon;
      return (
        <motion.div key={card.key} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
          className="p-4 rounded-xl border border-slate-200 bg-white shadow-sm flex flex-col" data-testid={`kpi-${card.key}`}>
          <div className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-slate-500">
              <Icon className="w-3.5 h-3.5 text-[#425B76]" />{card.label}
            </span>
            <InfoTip text={[card.def, ...GROUP_DEFS]} testId={`kpi-${card.key}-info`} />
          </div>
          <p className="text-2xl font-bold text-slate-900 mt-2" data-testid={`kpi-${card.key}-value`}>{display(card, kpis[card.key])}</p>
          <p className="text-xs text-slate-500 mt-1 flex-1">{card.hint(kpis, settings)}</p>
          {previous && (
            <div className="mt-2 flex items-center gap-1.5">
              <Delta card={card} cur={kpis[card.key]} prev={previous[card.key]} testId={`kpi-${card.key}-delta`} />
              <span className="text-[11px] text-slate-400">vs prev ({display(card, previous[card.key])})</span>
            </div>
          )}
        </motion.div>
      );
    })}
  </div>
);
